// pages/saolait/Control.js
// Danh sách các control dùng trong hệ thống form động (search.json, addbook.json, mybook.json...)
// Bên trái: menu chọn control. Bên phải: mô tả + bảng tham số cấu hình + ví dụ JSON.

import React, { useState } from "react";

// ---- Tô màu cú pháp JSON cho các khối code ----
const jsonToken = {
  key: { color: "#7dd3fc" },      // "tên": (khoá)
  string: { color: "#86efac" },   // "giá trị"
  number: { color: "#fca5a5" },   // số
  keyword: { color: "#c4b5fd" },  // true / false / null
  punct: { color: "#cbd5e1" },    // { } [ ] , :
  comment: { color: "#64748b", fontStyle: "italic" }, // // ghi chú
};

function highlightJson(src) {
  const nodes = [];
  const re =
    /(\/\/[^\n]*)|("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?)|([{}\[\],:])/g;
  let last = 0;
  let i = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) nodes.push(src.slice(last, m.index));
    if (m[1]) {
      nodes.push(<span key={i++} style={jsonToken.comment}>{m[1]}</span>);
    } else if (m[2]) {
      const colon = m[2].lastIndexOf(":");
      nodes.push(<span key={i++} style={jsonToken.key}>{m[2].slice(0, colon)}</span>);
      nodes.push(<span key={i++} style={jsonToken.punct}>{m[2].slice(colon)}</span>);
    } else if (m[3]) {
      nodes.push(<span key={i++} style={jsonToken.string}>{m[3]}</span>);
    } else if (m[4]) {
      nodes.push(<span key={i++} style={jsonToken.keyword}>{m[4]}</span>);
    } else if (m[5]) {
      nodes.push(<span key={i++} style={jsonToken.number}>{m[5]}</span>);
    } else if (m[6]) {
      nodes.push(<span key={i++} style={jsonToken.punct}>{m[6]}</span>);
    }
    last = re.lastIndex;
  }
  if (last < src.length) nodes.push(src.slice(last));
  return nodes;
}

// ------- Dữ liệu control -------
// Mỗi control: { type, group, usedIn[], desc, params[{name, req, desc}], example }
const CONTROLS = [
  {
    type: "taskbar",
    group: "Bố cục",
    usedIn: ["search.json", "addbook.json", "mybook.json"],
    desc: "Thanh tab điều hướng ở đầu trang (các icon chuyển giữa các form: Tìm kiếm, Đăng sách, Chat...).",
    params: [
      { name: "id", req: false, desc: "Định danh của taskbar." },
      { name: "tabs", req: true, desc: "Mảng tab. Mỗi tab: { layout_form, icon, description }." },
      { name: "tabs[].layout_form", req: true, desc: "Đường dẫn layout của form sẽ mở khi bấm tab." },
      { name: "tabs[].icon", req: true, desc: "URL ảnh icon của tab." },
      { name: "tabs[].description", req: false, desc: "Tooltip / nhãn mô tả tab." },
    ],
    example: `{
  "type": "taskbar", "id": "mainTaskbar",
  "tabs": [
    { "layout_form": ".../form/search", "icon": "https://.../Seach.JPG", "description": "Tìm kiếm" },
    { "layout_form": ".../form/addbook", "icon": "https://.../Add.JPG", "description": "Đăng sách" }
  ]
}`,
  },
  {
    type: "container",
    group: "Bố cục",
    usedIn: ["search.json", "addbook.json", "mybook.json"],
    desc: "Khối bao (div) để nhóm và bố cục các control con. Có thể lồng nhau tuỳ ý và có thể bấm được (clickable) nếu gắn action.",
    params: [
      { name: "children", req: false, desc: "Mảng control con bên trong." },
      { name: "style / styleName", req: false, desc: "Style trực tiếp hoặc tên style tái sử dụng." },
      { name: "text", req: false, desc: "Văn bản hiển thị (hỗ trợ placeholder {field})." },
      { name: "className", req: false, desc: "Tên class CSS gắn thêm." },
      { name: "action", req: false, desc: "{ type: 'custom', command } — biến container thành nút bấm." },
      { name: "condition", req: false, desc: "Chỉ hiển thị khi điều kiện đúng." },
    ],
    example: `{
  "type": "container",
  "style": { "display": "flex", "gap": "8px" },
  "children": [ ... ]
}`,
  },
  {
    type: "heading",
    group: "Hiển thị",
    usedIn: ["search.json", "addbook.json", "mybook.json"],
    desc: "Tiêu đề h1–h6.",
    params: [
      { name: "level", req: false, desc: "Cấp tiêu đề 1–6 (mặc định 2)." },
      { name: "text", req: true, desc: "Nội dung tiêu đề (hỗ trợ {field})." },
      { name: "style / styleName", req: false, desc: "Style của tiêu đề." },
    ],
    example: `{ "type": "heading", "level": 2, "text": "Đăng sách",
  "style": { "textAlign": "center" } }`,
  },
  {
    type: "label",
    group: "Nhập liệu",
    usedIn: ["addbook.json"],
    desc: "Nhãn cho một trường nhập liệu.",
    params: [
      { name: "htmlFor", req: true, desc: "id của input mà nhãn gắn tới." },
      { name: "text", req: true, desc: "Nội dung nhãn." },
      { name: "style / styleName", req: false, desc: "Style của nhãn." },
    ],
    example: `{ "type": "label", "htmlFor": "bookName", "text": "Tên sách" }`,
  },
  {
    type: "input",
    group: "Nhập liệu",
    usedIn: ["search.json", "addbook.json"],
    desc: "Ô nhập. Đổi kiểu qua inputType: text, email, password, file... Với file sẽ render ô chọn tệp.",
    params: [
      { name: "id", req: true, desc: "Định danh; cũng là key lưu giá trị trong form." },
      { name: "field", req: false, desc: "Tên trường dữ liệu khi gửi lên." },
      { name: "inputType", req: false, desc: "text (mặc định) | email | password | file | number..." },
      { name: "placeholder", req: false, desc: "Gợi ý trong ô nhập." },
      { name: "required", req: false, desc: "true = bắt buộc nhập." },
      { name: "accept", req: false, desc: "Chỉ dùng khi inputType='file', vd 'image/*'." },
      { name: "style / styleName", req: false, desc: "Style của ô nhập." },
    ],
    example: `{ "type": "input", "id": "bookName", "field": "book_name",
  "inputType": "text", "placeholder": "Nhập tên sách", "required": true }`,
  },
  {
    type: "textarea",
    group: "Nhập liệu",
    usedIn: ["addbook.json"],
    desc: "Ô nhập nhiều dòng.",
    params: [
      { name: "id", req: true, desc: "Định danh / key lưu giá trị." },
      { name: "field", req: false, desc: "Tên trường dữ liệu." },
      { name: "rows", req: false, desc: "Số dòng hiển thị (mặc định 3)." },
      { name: "placeholder", req: false, desc: "Gợi ý trong ô nhập." },
      { name: "required", req: false, desc: "true = bắt buộc nhập." },
      { name: "style / styleName", req: false, desc: "Style của ô nhập." },
    ],
    example: `{ "type": "textarea", "id": "note", "field": "note",
  "placeholder": "Nhập chú thích", "rows": 3 }`,
  },
  {
    type: "imagePreview",
    group: "Nhập liệu",
    usedIn: ["addbook.json"],
    desc: "Xem trước ảnh vừa chọn từ một input file. Tự ẩn khi chưa có ảnh.",
    params: [
      { name: "htmlFor", req: true, desc: "id của input file cần xem trước." },
      { name: "style", req: false, desc: "Style khung bao." },
      { name: "imgStyle", req: false, desc: "Style riêng cho thẻ <img>." },
    ],
    example: `{ "type": "imagePreview", "htmlFor": "image",
  "imgStyle": { "maxHeight": "200px", "borderRadius": "8px" } }`,
  },
  {
    type: "fileNameDisplay",
    group: "Nhập liệu",
    usedIn: ["addbook.json"],
    desc: "Hiển thị tên tệp đã chọn từ một input file. Tự ẩn khi chưa chọn.",
    params: [
      { name: "htmlFor", req: true, desc: "id của input file." },
      { name: "style", req: false, desc: "Style dòng chữ." },
    ],
    example: `{ "type": "fileNameDisplay", "htmlFor": "image" }`,
  },
  {
    type: "button",
    group: "Hành động",
    usedIn: ["search.json", "addbook.json", "mybook.json"],
    desc: "Nút bấm. Hai chế độ: gửi form (submit) hoặc chạy lệnh tuỳ biến (action custom). Có thể hiển thị icon thay chữ.",
    params: [
      { name: "id", req: false, desc: "Định danh nút." },
      { name: "buttonType", req: false, desc: "'submit' để gửi form; mặc định 'button'." },
      { name: "text", req: false, desc: "Chữ trên nút (hỗ trợ {field})." },
      { name: "submittingText", req: false, desc: "Chữ hiển thị khi đang gửi." },
      { name: "dataCollection", req: false, desc: "Đường dẫn kho dữ liệu để lưu khi submit." },
      { name: "action", req: false, desc: "Gọi lệnh riêng của trang thay vì gửi form. Dạng { \"type\": \"custom\", \"command\": \"<tênLệnh>\" }. Tên lệnh do bạn tự đặt; trang React bắt lệnh này qua onCustomAction và tự xử lý. Xem giải thích chi tiết bên dưới." },
      { name: "icon / iconStyle", req: false, desc: "URL icon và style của icon (thay cho text)." },
      { name: "disabledStyle", req: false, desc: "Style áp dụng khi nút bị vô hiệu (đang gửi)." },
      { name: "condition", req: false, desc: "Chỉ hiển thị khi điều kiện đúng." },
    ],
    example: `{ "type": "button", "id": "submitBtn", "buttonType": "submit",
  "text": "Lưu", "submittingText": "Đang lưu...",
  "dataCollection": "book/.../book_items_noa_new" }`,
    note: () => <ButtonActionNote />,
  },
  {
    type: "image",
    group: "Hiển thị",
    usedIn: ["search.json", "mybook.json"],
    desc: "Hiển thị ảnh. src hỗ trợ placeholder nên hay dùng trong thẻ dữ liệu (card).",
    params: [
      { name: "src", req: true, desc: "URL ảnh, hỗ trợ {field} vd '{image_url}'." },
      { name: "alt", req: false, desc: "Văn bản thay thế." },
      { name: "style / styleName", req: false, desc: "Style của ảnh." },
      { name: "condition", req: false, desc: "Vd '{image_url}' — chỉ hiện khi có ảnh." },
    ],
    example: `{ "type": "image", "styleName": "postImageStyle",
  "src": "{image_url}", "condition": "{image_url}" }`,
  },
  {
    type: "feed",
    group: "Dữ liệu",
    usedIn: ["search.json", "mybook.json"],
    desc: "Danh sách lặp: lấy một mảng dữ liệu từ context (dataKey) rồi render mỗi phần tử theo mẫu cardLayout. Trong cardLayout dùng {field} để lấy dữ liệu từng item.",
    params: [
      { name: "dataKey", req: true, desc: "Tên mảng dữ liệu trong context, vd 'filteredBooks'." },
      { name: "cardLayout", req: true, desc: "Mẫu (một control, thường là container) để render mỗi item." },
      { name: "style / styleName", req: false, desc: "Style khung bao danh sách (vd grid, gap)." },
    ],
    example: `{
  "type": "feed", "dataKey": "filteredBooks",
  "style": { "display": "grid", "gap": "16px" },
  "cardLayout": {
    "type": "container", "styleName": "postCardStyle",
    "children": [
      { "type": "container", "styleName": "bookTitleStyle", "text": "{book_name}" }
    ]
  }
}`,
  },
  {
    type: "pagination",
    group: "Dữ liệu",
    usedIn: ["search.json"],
    desc: "Thanh phân trang (Đầu / Trước / số trang / Sau / Cuối). Số trang lấy từ context; tự ẩn khi chỉ có 1 trang.",
    params: [
      { name: "(không cần tham số)", req: false, desc: "Trang lấy currentPage/totalPages/onPageChange từ context. Kiểu nút dùng style 'replySubmitButtonStyle'." },
      { name: "style", req: false, desc: "Style khung bao thanh phân trang." },
    ],
    example: `{ "type": "pagination" }`,
  },
  {
    type: "commentsTree",
    group: "Dữ liệu",
    usedIn: ["search.json", "mybook.json"],
    desc: "Khối bình luận nhiều cấp (comment + reply lồng nhau) cho một item. Style lấy theo các key: commentItemStyle, replyItemStyle, replyInputStyle...",
    params: [
      { name: "id", req: false, desc: "Định danh, thường 'comments-{book_id}'." },
      { name: "dataCollection", req: false, desc: "Kho dữ liệu để lưu bình luận/reply." },
      { name: "style", req: false, desc: "Style khung bao ngoài của cây bình luận." },
    ],
    example: `{ "type": "commentsTree", "id": "comments-{book_id}",
  "dataCollection": "book/.../commentBtn",
  "style": { "padding": "0 14px 14px 14px" } }`,
  },
  {
    type: "userHoverCard",
    group: "Người dùng",
    usedIn: ["search.json"],
    desc: "Tên người dùng có thẻ thông tin hiện khi rê chuột (avatar, trường học, nút mở chi tiết/chat).",
    params: [
      { name: "targetUid", req: true, desc: "UID người dùng, hỗ trợ {owner_id}." },
      { name: "displayName", req: true, desc: "Tên hiển thị, hỗ trợ {field}." },
      { name: "avatarUrl", req: false, desc: "URL avatar." },
      { name: "school", req: false, desc: "Tên trường." },
      { name: "nameStyleName", req: false, desc: "Tên style áp cho nút tên." },
    ],
    example: `{ "type": "userHoverCard", "targetUid": "{owner_id}",
  "displayName": "{ownerDisplayName}", "avatarUrl": "{ownerAvatarUrl}",
  "school": "{ownerSchool}", "nameStyleName": "ownerNameButtonStyle" }`,
  },
  {
    type: "userDetailDialog",
    group: "Người dùng",
    usedIn: ["search.json", "mybook.json"],
    desc: "Hộp thoại chi tiết người dùng. Chỉ cần khai báo một lần; nội dung và trạng thái đóng/mở do context điều khiển.",
    params: [
      { name: "(không cần tham số)", req: false, desc: "Đọc showUserDetailDialog, detailName, detailAvatar, detailSchool từ context." },
    ],
    example: `{ "type": "userDetailDialog" }`,
  },
  {
    type: "chatWidget",
    group: "Người dùng",
    usedIn: ["search.json", "mybook.json"],
    desc: "Cửa sổ chat nổi ở góc phải màn hình. Style theo các key chatWidgetStyle, chatBubbleStyle... Trạng thái do context điều khiển.",
    params: [
      { name: "(không cần tham số)", req: false, desc: "Đọc dữ liệu chat và người đang chat từ context." },
    ],
    example: `{ "type": "chatWidget" }`,
  },
  {
    type: "recentConversations",
    group: "Người dùng",
    usedIn: ["(tuỳ chọn)"],
    desc: "Danh sách các cuộc trò chuyện gần đây. Bấm vào một mục để mở chat với người đó.",
    params: [
      { name: "(không cần tham số)", req: false, desc: "Đọc danh sách hội thoại từ context; style theo recentConversation*Style." },
    ],
    example: `{ "type": "recentConversations" }`,
  },
  {
    type: "dialog",
    group: "Bố cục",
    usedIn: ["search.json"],
    desc: "Hộp thoại (modal) tuỳ biến. Ẩn/hiện theo một cờ trong context (contextKey). Bấm nền tối để đóng.",
    params: [
      { name: "id", req: false, desc: "Định danh hộp thoại." },
      { name: "contextKey", req: true, desc: "Tên cờ trong context điều khiển ẩn/hiện, vd 'showAdvancedSearch'." },
      { name: "closeCommand", req: false, desc: "Lệnh custom gọi khi đóng (nếu không sẽ tự set cờ về false)." },
      { name: "children", req: true, desc: "Nội dung bên trong hộp thoại." },
      { name: "style / styleName", req: false, desc: "Style của khung hộp thoại." },
    ],
    example: `{
  "type": "dialog", "id": "advanced-search-dialog",
  "contextKey": "showAdvancedSearch",
  "style": { "padding": "24px", "borderRadius": "16px" },
  "children": [ ... ]
}`,
  },
];

const styles = {
  page: { maxWidth: 1080, margin: "0 auto", padding: "24px 20px 64px", color: "#1f2937", fontSize: 16, textAlign: "left" },
  pageTitle: { fontSize: 30, fontWeight: 800, margin: "0 0 4px", color: "#111827" },
  pageSub: { color: "#6b7280", margin: "0 0 24px", fontSize: 15 },
  layout: { display: "grid", gridTemplateColumns: "260px 1fr", gap: 24, alignItems: "start" },
  sidebar: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#f8fafc",
    padding: 10,
    position: "sticky",
    top: 12,
    maxHeight: "calc(100vh - 24px)",
    overflowY: "auto",
  },
  groupTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    margin: "12px 8px 6px",
  },
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14.5,
    fontFamily: "inherit",
    color: "#111827",
  },
  menuItemActive: { background: "#2563eb", color: "#fff", fontWeight: 700 },
  detail: { border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff", padding: "22px 24px" },
  detailTitle: { fontSize: 24, fontWeight: 800, margin: "0 0 2px", color: "#1d4ed8" },
  typeTag: {
    display: "inline-block",
    background: "#eef2ff",
    color: "#4338ca",
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "monospace",
  },
  usedIn: { color: "#6b7280", fontSize: 13, margin: "8px 0 16px" },
  badge: {
    display: "inline-block",
    background: "#f1f5f9",
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: 12.5,
    marginRight: 6,
    marginTop: 4,
    fontFamily: "monospace",
    color: "#334155",
  },
  desc: { lineHeight: 1.6, margin: "0 0 18px" },
  sectionLabel: { fontSize: 14, fontWeight: 700, color: "#111827", margin: "20px 0 8px" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 14.5 },
  th: { border: "1px solid #e5e7eb", padding: "8px 10px", background: "#f3f4f6", textAlign: "left" },
  td: { border: "1px solid #e5e7eb", padding: "8px 10px", verticalAlign: "top" },
  paramName: { fontFamily: "monospace", color: "#be123c", fontWeight: 600 },
  reqYes: { color: "#dc2626", fontWeight: 700 },
  reqNo: { color: "#9ca3af" },
  pre: {
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
    overflowX: "auto",
    fontSize: 13.5,
    lineHeight: 1.5,
    margin: "8px 0 0",
    textAlign: "left",
    whiteSpace: "pre",
  },
  noteBox: {
    marginTop: 18,
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 10,
    padding: "14px 16px",
    fontSize: 14.5,
    lineHeight: 1.6,
  },
  noteTitle: { fontWeight: 700, margin: "0 0 8px", color: "#92400e" },
  inlineCode: {
    background: "#f1f5f9",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 13.5,
    fontFamily: "monospace",
    color: "#be123c",
  },
  notePre: {
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: 8,
    padding: "12px 14px",
    overflowX: "auto",
    fontSize: 13,
    lineHeight: 1.5,
    margin: "8px 0",
    textAlign: "left",
    whiteSpace: "pre",
  },
};

// Khối giải thích chi tiết cơ chế action custom cho nút bấm
const ButtonActionNote = () => (
  <div>
    <p style={styles.noteTitle}>Cách dùng action "custom" (lệnh riêng của trang)</p>
    <p style={{ margin: "0 0 8px" }}>
      Dùng khi bấm nút cần làm việc gì đó <strong>không phải gửi form</strong> — ví dụ mở/đóng hộp thoại,
      lọc dữ liệu, đánh dấu trạng thái. JSON chỉ khai báo <em>tên lệnh</em>; phần <em>xử lý</em> nằm ở trang React.
      Luồng gồm 2 đầu:
    </p>
    <p style={{ margin: "8px 0 2px" }}>
      <strong>1) Trong JSON</strong> — đặt tên lệnh vào <span style={styles.inlineCode}>command</span>:
    </p>
    <pre style={styles.notePre}>
      <code>{highlightJson(`{
  "type": "button", "id": "toggleAdvancedBtn",
  "text": "Nâng cao",
  "action": { "type": "custom", "command": "toggleAdvancedSearch" }
}`)}</code>
    </pre>
    <p style={{ margin: "8px 0 2px" }}>
      <strong>2) Trong trang React</strong> — viết hàm nhận lệnh và truyền cho{" "}
      <span style={styles.inlineCode}>DynamicFormCore</span> qua prop{" "}
      <span style={styles.inlineCode}>onCustomAction</span>. So khớp theo tên lệnh rồi xử lý:
    </p>
    <pre style={styles.notePre}>
      <code>{`const handleCustomAction = (command, elementId, itemContext, formData) => {
  if (command === 'toggleAdvancedSearch') {
    setShowAdvancedSearch(prev => !prev);      // mở/đóng dialog
  } else if (command === 'submitAdvancedSearch') {
    const book = formData['advSearchBookInput'];  // đọc giá trị đang nhập
    // ... xử lý tìm kiếm
  }
};

<DynamicFormCore schema={schema} onCustomAction={handleCustomAction} ... />`}</code>
    </pre>
    <p style={{ margin: "8px 0 2px" }}>
      Khi bấm nút, bộ render gọi <span style={styles.inlineCode}>onCustomAction</span> với 4 thông tin:
    </p>
    <ul style={{ margin: "4px 0 8px", paddingLeft: 20 }}>
      <li><span style={styles.inlineCode}>command</span>: tên lệnh khai trong JSON (dùng để phân biệt nút nào).</li>
      <li><span style={styles.inlineCode}>elementId</span>: <span style={styles.inlineCode}>id</span> của nút vừa bấm.</li>
      <li><span style={styles.inlineCode}>itemContext</span>: dữ liệu của dòng/thẻ chứa nút (khi nút nằm trong <span style={styles.inlineCode}>feed</span>). Vd nút "Đã giao dịch" ở mybook dùng cái này để biết cuốn sách nào.</li>
      <li><span style={styles.inlineCode}>formData</span>: toàn bộ giá trị đang nhập trong form (theo <span style={styles.inlineCode}>id</span>).</li>
    </ul>
    <p style={{ margin: "8px 0 0" }}>
      <strong>Lưu ý:</strong> đặt <span style={styles.inlineCode}>command</span> tuỳ ý bạn, nhưng trang React
      <strong> phải có nhánh xử lý đúng tên đó</strong> — nếu không, bấm nút sẽ không có gì xảy ra.
      Các lệnh đang dùng: <span style={styles.inlineCode}>toggleAdvancedSearch</span>,{" "}
      <span style={styles.inlineCode}>closeAdvancedSearch</span>,{" "}
      <span style={styles.inlineCode}>submitAdvancedSearch</span> (search.json),{" "}
      <span style={styles.inlineCode}>toggleRelease</span> (mybook.json).
    </p>
  </div>
);

const Control = () => {
  const [selected, setSelected] = useState(CONTROLS[0].type);
  const current = CONTROLS.find((c) => c.type === selected) || CONTROLS[0];

  // Nhóm control theo group để hiển thị menu
  const groups = CONTROLS.reduce((acc, c) => {
    (acc[c.group] = acc[c.group] || []).push(c);
    return acc;
  }, {});

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Control — các thành phần dùng trong form động</h1>
      <p style={styles.pageSub}>
        Danh sách các control (kiểu <code>type</code>) dùng trong <strong>search.json</strong>,{" "}
        <strong>addbook.json</strong>, <strong>mybook.json</strong>… và hướng dẫn cấu hình từng tham số.
        Chọn một control ở menu bên trái để xem chi tiết.
      </p>

      <div style={styles.layout}>
        {/* MENU TRÁI */}
        <div style={styles.sidebar}>
          {Object.keys(groups).map((g) => (
            <div key={g}>
              <div style={styles.groupTitle}>{g}</div>
              {groups[g].map((c) => (
                <button
                  key={c.type}
                  onClick={() => setSelected(c.type)}
                  style={{
                    ...styles.menuItem,
                    ...(c.type === selected ? styles.menuItemActive : {}),
                  }}
                >
                  {c.type}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* CHI TIẾT PHẢI */}
        <div style={styles.detail}>
          <h2 style={styles.detailTitle}>
            <span style={styles.typeTag}>{current.type}</span>
          </h2>
          <div style={styles.usedIn}>
            Dùng trong:{" "}
            {current.usedIn.map((f) => (
              <span key={f} style={styles.badge}>{f}</span>
            ))}
          </div>

          <p style={styles.desc}>{current.desc}</p>

          <div style={styles.sectionLabel}>Tham số cấu hình</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Tham số</th>
                <th style={{ ...styles.th, width: 90, textAlign: "center" }}>Bắt buộc</th>
                <th style={styles.th}>Ý nghĩa</th>
              </tr>
            </thead>
            <tbody>
              {current.params.map((p) => (
                <tr key={p.name}>
                  <td style={styles.td}>
                    <span style={styles.paramName}>{p.name}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    {p.req ? <span style={styles.reqYes}>Có</span> : <span style={styles.reqNo}>—</span>}
                  </td>
                  <td style={styles.td}>{p.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={styles.sectionLabel}>Ví dụ JSON</div>
          <pre style={styles.pre}>
            <code>{highlightJson(current.example)}</code>
          </pre>

          {current.note && (
            <div style={styles.noteBox}>{current.note()}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Control;
